/**
 * Shared utilities for handling session expiration (401 errors)
 *
 * This module consolidates the 401 error handling logic that was previously
 * duplicated across multiple files:
 * - src/services/api.js
 * - src/services/tempChatService.js
 * - src/lib/ai-providers/backend-provider-service.js (2 places)
 * - src/lib/utils/ai/streaming/databaseOperations.js
 *
 * Note: src/lib/supabase.js has its own 401 handling that directly redirects
 * without going through authStore, as it handles Supabase-specific cases.
 */

import useAuthStore from "@/store/authStore";

/**
 * Check if an error indicates session expiration (401 Unauthorized)
 *
 * @param {Error|Response|Object} error - The error to check. Can be:
 *   - An axios error with error.response.status
 *   - A fetch Response with error.status
 *   - A Supabase error with error.code or error.message
 * @returns {boolean} - True if this is a 401 auth error
 */
export function isAuthError(error) {
  // Check for HTTP 401 status (axios error.response.status or fetch Response.status)
  if (error?.response?.status === 401) return true;
  if (error?.status === 401) return true;

  // Check for Supabase error patterns
  if (error?.message?.includes("JWT") || error?.message?.includes("token")) {
    return true;
  }

  // Check for 401 in error code (some Supabase errors)
  if (error?.code === "401" || error?.code === 401) return true;

  return false;
}

/**
 * Handle 401 errors by triggering session expiration
 *
 * This function:
 * 1. Logs a warning message
 * 2. Calls handleSessionExpired() from the auth store
 *
 * The handleSessionExpired function will:
 * - Clear the session from Supabase
 * - Clear localStorage
 * - Reset auth state
 * - Redirect to login page with reason=session_expired
 *
 * @param {string} [source='Unknown'] - The source of the error for logging
 */
export function handleAuthError(source = "Unknown") {
  console.warn(`[${source}] 401 Unauthorized - session may have expired`);
  const { handleSessionExpired } = useAuthStore.getState();
  handleSessionExpired();
}

/**
 * Check if error is a 401 and handle it if so
 *
 * Convenience function that combines isAuthError and handleAuthError.
 * Returns true if it was a 401 error (so caller knows to stop processing).
 *
 * @param {Error|Response|Object} error - The error to check
 * @param {string} [source='Unknown'] - The source of the error for logging
 * @returns {boolean} - True if this was a 401 error that was handled
 */
export function handleSessionExpiredError(error, source = "Unknown") {
  if (isAuthError(error)) {
    handleAuthError(source);
    return true;
  }
  return false;
}
